import { useRef, useState, useEffect, useCallback } from "react";
import { Loader2, Check } from "lucide-react";
import { mediaUrl } from "../lib/api";
import { fmt } from "../lib/format";

export const WaveformTrimmer = ({ clip, show, onSave, saving }) => {
  const duration = show?.duration_sec || clip.end_sec + 5;
  const trackRef = useRef(null);
  const [start, setStart] = useState(clip.start_sec);
  const [end, setEnd] = useState(clip.end_sec);
  const [drag, setDrag] = useState(null);

  const pct = (s) => `${Math.min(100, Math.max(0, (s / duration) * 100))}%`;

  const move = useCallback((clientX) => {
    if (!drag || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const t = Math.round(ratio * duration * 10) / 10;
    if (drag === "start") setStart(Math.min(t, end - 2));
    else setEnd(Math.max(t, start + 2));
  }, [drag, duration, start, end]);

  useEffect(() => {
    if (!drag) return;
    const mm = (e) => move(e.clientX ?? e.touches?.[0]?.clientX);
    const mu = () => setDrag(null);
    window.addEventListener("pointermove", mm);
    window.addEventListener("pointerup", mu);
    return () => {
      window.removeEventListener("pointermove", mm);
      window.removeEventListener("pointerup", mu);
    };
  }, [drag, move]);

  return (
    <div data-testid={`trim-editor-${clip.id}`} className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">Drag the handles to trim</p>
        <p className="font-mono text-[11px] text-muted-foreground">{fmt(start)} → {fmt(end)} · {Math.round(end - start)}s</p>
      </div>

      <div
        ref={trackRef}
        className="relative h-24 w-full select-none overflow-hidden border border-border bg-black"
        style={{ touchAction: "none" }}
      >
        {show?.waveform_path && (
          <img
            src={mediaUrl(show.waveform_path)}
            alt="waveform"
            draggable={false}
            className="pointer-events-none absolute inset-0 h-full w-full object-fill opacity-30"
          />
        )}
        {/* selected region */}
        <div
          className="absolute top-0 bottom-0 border-x-2 border-accent bg-accent/15"
          style={{ left: pct(start), width: `calc(${pct(end)} - ${pct(start)})` }}
        />
        {/* start handle */}
        <div
          data-testid={`trim-handle-start-${clip.id}`}
          onPointerDown={() => setDrag("start")}
          className="absolute top-0 bottom-0 z-10 flex w-4 -translate-x-1/2 cursor-ew-resize items-center justify-center bg-accent"
          style={{ left: pct(start) }}
        >
          <div className="h-8 w-0.5 bg-black/60" />
        </div>
        {/* end handle */}
        <div
          data-testid={`trim-handle-end-${clip.id}`}
          onPointerDown={() => setDrag("end")}
          className="absolute top-0 bottom-0 z-10 flex w-4 -translate-x-1/2 cursor-ew-resize items-center justify-center bg-accent"
          style={{ left: pct(end) }}
        >
          <div className="h-8 w-0.5 bg-black/60" />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="font-mono text-[10px] text-muted-foreground">FULL SHOW · {fmt(duration)}</span>
        <button
          data-testid={`trim-save-${clip.id}`}
          onClick={() => onSave(start, end)}
          disabled={saving}
          className="flex items-center gap-1.5 bg-accent px-5 py-2 text-xs font-semibold uppercase tracking-widest text-accent-foreground transition-colors hover:bg-accent/90 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {saving ? "Re-cutting" : "Save Cut"}
        </button>
      </div>
    </div>
  );
};
